import { CartAddEvent } from '@theme/events';

/**
 * Document-delegatie voor PDP-blokken zónder custom element (M4 uit docs/PDP-BEVINDINGEN.md).
 *
 * Waarom: bij een combined-listing-switch morpht het thema de hele <main>. Inline
 * <script>-blokken in blocks/snippets draaien daarna nooit (opnieuw) — hergebruikte
 * nodes houden verouderde closures, nieuw ingevoegde nodes krijgen nooit een listener.
 * Delegatie vanuit dit altijd-geladen asset is daar immuun voor, óók voor blokken die
 * pas ná een switch verschijnen. Regels: state altijd op klik-moment uit de DOM lezen
 * (dataset/attributen), nooit uit closures.
 *
 * Dekt:
 * - Meetverzekering-chip (blocks/product-meetverzekering.liquid)
 * - Staal-add-to-cart-knop (snippets/button.liquid)
 */

/**
 * Voegt het staal van de knop toe aan de cart. Leest `data-variant-id` op klik-moment,
 * zodat na een combined-listing-morph altijd het staal van het huidige product wordt
 * toegevoegd (de oude inline-closure hield de variant van het vorige product vast).
 *
 * @param {HTMLButtonElement} button
 */
async function addStaalToCart(button) {
  if (button.disabled || button.hasAttribute('data-loading')) return;

  const variantId = Number.parseInt(button.dataset.variantId ?? '', 10);
  if (!Number.isFinite(variantId)) return;

  button.disabled = true;
  button.setAttribute('data-loading', 'true');

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to add item to cart');
    }

    const cart = await fetch('/cart.js').then((r) => r.json());

    document.dispatchEvent(
      new CartAddEvent(cart, `product-button-${button.dataset.blockId}`, {
        source: 'product-button',
        itemCount: cart.item_count,
        variantId: String(variantId),
      })
    );

    button.removeAttribute('data-loading');
    button.setAttribute('data-added', 'true');

    // Reset na de animatie (zelfde duur als de reguliere add-to-cart-knop)
    setTimeout(() => {
      button.removeAttribute('data-added');
      button.disabled = false;
    }, 3000);
  } catch (error) {
    console.error('Error adding to cart:', error);
    button.removeAttribute('data-loading');
    button.disabled = false;
  }
}

/** @param {Element} chip */
function toggleMeetverzekering(chip) {
  const panelId = chip.getAttribute('aria-controls');
  const panel = panelId ? document.getElementById(panelId) : null;
  if (!panel) return;

  const open = chip.getAttribute('aria-expanded') === 'true';
  chip.setAttribute('aria-expanded', String(!open));
  panel.hidden = open;
}

// Capture-fase: de oude inline staal-handler deed stopPropagation op de knop zelf;
// alleen vanuit capture kunnen we dat gedrag (geen andere handlers) behouden.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const chip = target.closest('.mv__chip[aria-controls]');
    if (chip) {
      toggleMeetverzekering(chip);
      return;
    }

    const staalButton = target.closest('button.product-button-add-to-cart[data-variant-id]');
    if (staalButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      addStaalToCart(staalButton);
    }
  },
  { capture: true }
);
